import { ensureNoUndefinedProperties } from '@/Shared/Helper/EnsureNoUndefinedProperties'
import CompanyID from '@/App/Company/Entity/CompanyID'
import {
    AutomationActionTypeEnum,
    AutomationActionTypeEnumData,
    AutomationStateEnum,
    AutomationStateEnumData,
    AutomationTriggerTypeEnum,
    AutomationTriggerTypeEnumData,
    type AutomationActionDataSendAttendeeMeetingReminderEmailType,
    type AutomationActionDataSendHostMeetingReminderEmailType,
    type AutomationActionTypeEnumDataEntry,
    type AutomationRawData,
    type AutomationStateEnumDataEntry,
    type AutomationTriggerDataXMinutesBeforeMeetingStartsType,
    type AutomationTriggerTypeEnumDataEntry,
} from '@/Shared/AutoGenerated/Main'
import AutomationID from '@/App/Automation/Entity/AutomationID'

export default class Automation {
    constructor(
        public readonly id: AutomationID,
        public readonly companyId: CompanyID,
        public readonly name: string,
        public readonly state: AutomationStateEnumDataEntry,
        public readonly actionType: AutomationActionTypeEnumDataEntry,
        public readonly actionData:
            | AutomationActionDataSendHostMeetingReminderEmailType
            | AutomationActionDataSendAttendeeMeetingReminderEmailType,
        public readonly triggerType: AutomationTriggerTypeEnumDataEntry,
        public readonly triggerData: AutomationTriggerDataXMinutesBeforeMeetingStartsType,
    ) {
        ensureNoUndefinedProperties(this)
    }

    hasActionSettings(): boolean {
        return (
            typeof this.actionData === 'object' &&
            Object.keys(this.actionData.data).length > 0
        )
    }

    hasTriggerSettings(): boolean {
        return (
            typeof this.triggerData === 'object' &&
            Object.keys(this.triggerData.data).length > 0
        )
    }

    isEnabled(): boolean {
        return this.state.key === AutomationStateEnum.Enabled
    }

    isDisabled(): boolean {
        return this.isEnabled() === false
    }

    // ------------------------
    // Type narrowing getters - Action
    // ------------------------

    getSendHostMeetingReminderActionData(): AutomationActionDataSendHostMeetingReminderEmailType {
        if (
            this.actionType.key !==
            AutomationActionTypeEnum.SendHostMeetingReminderEmail
        ) {
            throw new Error('Action data and type mismatch')
        }

        return this
            .actionData as AutomationActionDataSendHostMeetingReminderEmailType
    }

    getSendAttendeeMeetingReminderActionData(): AutomationActionDataSendAttendeeMeetingReminderEmailType {
        if (
            this.actionType.key !==
            AutomationActionTypeEnum.SendAttendeeMeetingReminderEmail
        ) {
            throw new Error('Action data and type mismatch')
        }

        return this
            .actionData as AutomationActionDataSendAttendeeMeetingReminderEmailType
    }

    // ------------------------
    // Type narrowing getters - Triggers
    // ------------------------

    getXMinutesBeforeMeetingStartsTriggerData(): AutomationTriggerDataXMinutesBeforeMeetingStartsType {
        if (
            this.triggerType.key !==
            AutomationTriggerTypeEnum.XMinutesBeforeMeetingStarts
        ) {
            throw new Error('Trigger data and type mismatch')
        }

        return this
            .triggerData as AutomationTriggerDataXMinutesBeforeMeetingStartsType
    }

    // ------------------------
    // Hydration
    // ------------------------

    static hydrateSingle(entityData: AutomationRawData): Automation {
        return new Automation(
            new AutomationID(entityData.id),
            new CompanyID(entityData.companyId),
            entityData.name,
            AutomationStateEnumData[entityData.state as AutomationStateEnum],
            AutomationActionTypeEnumData[
                entityData.actionType as AutomationActionTypeEnum
            ],
            entityData.actionData,
            AutomationTriggerTypeEnumData[
                entityData.triggerType as AutomationTriggerTypeEnum
            ],
            entityData.triggerData,
        )
    }

    static hydrateMultiple(entitiesData: AutomationRawData[]): Automation[] {
        return entitiesData.map((entityData) => this.hydrateSingle(entityData))
    }
}
